import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ImageOff, Loader2, Minus, PackageOpen, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button as ShadButton } from "@/components/ui/button";
import { Input as ShadInput } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CURRENCY_SYMBOL, siteConfig, useSiteConfig } from "../lib/site-config.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  业务基础件
 * ═══════════════════════════════════════════════════════════════
 *
 * 底层用 shadcn/ui（`components/ui/*`），这一层只做**业务语义的包装**：
 * 价格怎么排版、订单状态怎么翻成人话、图挂了显示什么。
 *
 * ⚠️ **导出签名保持不变**：25 个页面在用它们，
 *    换底座不该变成一次 25 个文件的大改。
 *
 * 🔴 点击区域最小 44px。建材的买家很多在工地上、戴着手套用手机 ——
 *    按钮做小一点他就点不中。
 */

/**
 * 金额符号。
 *
 * 🔴 **只换符号，绝不折算金额。**
 *    库里所有金额都存**分**，它们是哪种货币的分由后台的
 *    `settings.appearance.currency` 声明。折算意味着页面显示 NT$
 *    而实际按 CNY 扣钱 —— 那是资金事故，不是显示问题。
 *    真要做多币种（同一批商品对不同地区显示不同价）是另一件事：
 *    要汇率表、要下单时锁汇率、要定结算按哪一种记账。
 *
 * ⚠️ 这是**函数不是常量** —— 后台改完配置接口回来时值会变，
 *    取一次存起来的话页面上会留着旧符号直到刷新。
 */
export function currencySymbol(): string {
  return CURRENCY_SYMBOL[siteConfig().currency];
}

export function fen(cents: number): string {
  return `${currencySymbol()}${(cents / 100).toFixed(2)}`;
}

/** 只要整数部分的写法（划线价、"省 ¥60" 这类）。 */
export function fenInt(cents: number): string {
  return `${currencySymbol()}${(cents / 100).toFixed(0)}`;
}

/**
 * 价格。
 *
 * ⚠️ 整数部分放大、小数缩小 —— 一眼扫过去看到的是"128"而不是"128.00"。
 *    `tabular-nums` 让不同价格的位数**竖着对齐**，列表里才不会参差。
 */
export function Price({ cents, className = "" }: { cents: number; className?: string }) {
  /** ⚠️ 订阅配置而不是读一次 —— 后台切了货币，页面上所有价格要立刻跟着换符号。 */
  const { currency } = useSiteConfig();
  const yuan = Math.floor(Math.abs(cents) / 100);
  const dec = String(Math.abs(cents) % 100).padStart(2, "0");
  const sign = cents < 0 ? "-" : "";
  return (
    <span className={cn("font-medium tabular-nums", className)}>
      <span className="text-[0.7em]">
        {sign}
        {CURRENCY_SYMBOL[currency]}
      </span>
      {yuan}
      <span className="text-[0.7em]">.{dec}</span>
    </span>
  );
}

/** 旧接口 → shadcn 的映射。`primary` 是本项目的叫法，shadcn 叫 `default`。 */
const VARIANT_MAP = {
  primary: "default",
  outline: "outline",
  ghost: "ghost",
  danger: "destructive",
  secondary: "secondary",
} as const;

export function Button({
  children,
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  className = "",
  ...rest
}: {
  children: ReactNode;
  variant?: keyof typeof VARIANT_MAP;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <ShadButton
      variant={VARIANT_MAP[variant]}
      size={size === "md" ? "default" : size}
      disabled={disabled || loading}
      className={className}
      {...rest}
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
    </ShadButton>
  );
}

export function Input({
  label,
  error,
  className = "",
  ...rest
}: { label?: string; error?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-[13px] text-muted-foreground">{label}</span>}
      <ShadInput
        className={cn(error && "border-destructive focus-visible:ring-destructive/30", className)}
        {...rest}
      />
      {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
    </label>
  );
}

/**
 * 加载中。
 *
 * ⚠️ 列表类页面传 `rows` 用**骨架屏**而不是转圈：
 *    骨架屏长得像真内容，加载完不会整页跳一下。
 */
export function Spinner({
  label = "加载中",
  rows,
  compact,
}: {
  label?: string;
  rows?: number;
  /** 网页版用。⚠️ 跟 `Empty` 同理：撑满一屏的高度只对手机成立。 */
  compact?: boolean;
}) {
  if (rows) {
    return (
      <div className="space-y-2 px-3 py-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex gap-3 rounded-xl border border-border bg-card p-3">
            <Skeleton className="size-20 shrink-0 rounded-lg" />
            <div className="flex-1 space-y-2 py-1">
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-3 w-2/5" />
              <Skeleton className="h-5 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  /**
   * ⚠️ 跟空态同一个道理：转圈固定在页面上方、下面一大片空白很难看。
   * ⚠️ 跟 `Empty` 用同一套高度（`min-h-[420px]`），
   *    这样加载完切成空态时**位置不跳** —— 跳一下会让人以为页面又刷新了一次。
   *    这里不画边框：转圈是瞬时状态，画个框反而像多闪了一个卡片出来。
   */
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 text-sm text-muted-foreground",
        compact ? "min-h-[420px]" : "min-h-[calc(100svh-9rem)]",
      )}
    >
      <Loader2 className="size-4 animate-spin" />
      {label}
    </div>
  );
}

/**
 * 空状态。
 *
 * ⚠️ 一律给一句「接下来能做什么」，而不是只说「没有数据」——
 *    用户看到空白页时最需要的是下一步动作。
 */
export function Empty({
  title,
  hint,
  action,
  compact,
  bare,
  fill,
}: {
  title: string;
  hint?: string;
  /** ⚠️ `to` 跳转、`onClick` 就地处理（比如"新增地址"要展开表单，没有目标页）。 */
  action?: { label: string; to?: string; onClick?: () => void };
  /**
   * 网页版用。
   *
   * 🔴 **默认那个"撑满一屏"的高度只对手机成立。**
   *    网页上空态是嵌在右侧内容区里的一块，撑满 100svh 会把按钮推到
   *    屏幕外面去 —— 这个坑在后台的「一键生成」按钮上真踩过一次
   *    （900 高的窗口里按钮落在 y=901）。
   */
  compact?: boolean;
  /**
   * 撑满**父容器**的高度（父容器要自己有确定的高度）。
   *
   * 🔴 **只有当父容器是一个"按视口算好高度的 flex 列"时才用它。**
   *    它发出的是 `flex-1` —— 在那种父容器里正好撑满剩下的高度；
   *    但**绝不能**把它用在跟左侧导航同一个 flex 行的地方：
   *    那导航一千多像素高，卡片会被拉到 1100px 直接溢出屏幕（真踩过）。
   *    `min-h-[420px]` 仍然在，所以父容器矮的时候也不会缩成一条。
   */
  fill?: boolean;
  /**
   * 已经在一张卡里了，别再画一层边框。
   * ⚠️ 目前没有这种用法，留这个口子是为了以后真嵌进 `Panel` 时不用再改组件。
   */
  bare?: boolean;
}) {
  return (
    /**
     * 🔴 **空态要撑满剩余高度并垂直居中，不能只加一个固定的上边距。**
     *    以前是 `py-16`（固定内边距），图标永远贴在页面上方 ——
     *    下面一大片空白，看着像页面没加载完。老板在后台连着圈了两页。
     *
     * 🔴 **网页版（`compact`）的空态是"这块内容是空的"，不是"页面是空的"。**
     *    所以它长成**一张卡**：满宽、有边框、里面上下居中。
     *    不画卡的话它就是几行字浮在内容区左上角 —— 横向看着偏左
     *    （父容器可能有 `max-w-*`）、纵向贴着标题栏，底下一大片空白。
     *
     * ⚠️ **用固定的 `min-h`，不要用 `flex-1` 去"填满剩余高度"。**
     *    试过 `flex-1`：后台的内容区跟**左侧导航**在同一个 flex 行里，
     *    而那个导航有二十来项、一千多像素高 —— 于是空态卡片被拉到 1100px，
     *    直接溢出屏幕，文字掉到视口下面去了，比贴在顶上还糟。
     *    420px 在 900 高的窗口里正好是一块，短屏笔记本上也不会挤爆。
     *
     * ⚠️ 手机版用 `svh` 不用 `vh` —— 地址栏收放时 `vh` 会跳，内容跟着抖。
     *    高度按**可用高度**算，不能拍一个 `60svh` 了事：容器是从标题栏下面
     *    开始的，`60svh` 会让内容整体压在上面 1/4 处。
     *    减掉的 9rem ≈ 标题栏 + 底部导航 + 一点呼吸空间。
     */
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 px-6 text-center",
        compact
          ? cn("min-h-[420px]", fill && "min-h-0 flex-1", !bare && "rounded-lg border border-border bg-background")
          : "min-h-[calc(100svh-9rem)]",
      )}
    >
      <div className="mb-1 flex size-16 items-center justify-center rounded-full bg-secondary">
        <PackageOpen className="size-7 text-muted-foreground" />
      </div>
      <p className="text-[15px] font-medium">{title}</p>
      {hint && (
        <p
          className={cn(
            "text-[13px] leading-relaxed text-muted-foreground",
            /**
             * ⚠️ 网页上一行 16rem 太窄，一句话会被折成四五行。
             * ⚠️ **别用 `ch` 量中文**：`1ch` 是字符「0」的宽度（半角），
             *    `46ch` 只放得下 23 个汉字 —— 一句 30 字的提示还是会折行，
             *    而且经常只折出最后一个字，看着像排版坏了。
             */
            compact ? "max-w-[34rem]" : "max-w-[16rem]",
          )}
        >
          {hint}
        </p>
      )}
      {action &&
        (action.to ? (
          <Link to={action.to} className="mt-3">
            <ShadButton variant="outline" size="sm">
              {action.label}
            </ShadButton>
          </Link>
        ) : (
          <ShadButton variant="outline" size="sm" className="mt-3" onClick={action.onClick}>
            {action.label}
          </ShadButton>
        ))}
    </div>
  );
}

/**
 * 错误块。
 *
 * ⚠️ 直接显示后端给的 message —— 后端错误合同里那句话本来就是写给用户看的
 *    （"库存不足，仅剩 3" 比 "操作失败" 有用得多）。
 */
export function ErrorBlock({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "出了点问题";
  return (
    <div className="mx-3 my-4 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3">
      <p className="text-[14px] leading-relaxed text-destructive break-anywhere">{message}</p>
      {onRetry && (
        <button
          className="mt-2 text-[13px] text-destructive underline underline-offset-2 active:opacity-60"
          onClick={onRetry}
        >
          重试
        </button>
      )}
    </div>
  );
}

/**
 * 数量加减。
 *
 * ⚠️ 加减各 44px，中间的数字**不做输入框** ——
 *    手机上点一下弹出数字键盘、盖住半个屏幕，很烦。
 */
export function QtyStepper({
  value,
  onChange,
  min = 1,
  max = 9999,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div className="inline-flex items-center overflow-hidden rounded-lg border border-border">
      <button
        className="flex size-10 items-center justify-center text-muted-foreground active:bg-secondary disabled:opacity-30"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label="减少"
      >
        <Minus className="size-4" />
      </button>
      <span className="w-10 text-center text-[15px] font-medium tabular-nums">{value}</span>
      <button
        className="flex size-10 items-center justify-center text-muted-foreground active:bg-secondary disabled:opacity-30"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label="增加"
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
}

/**
 * 订单状态徽标。
 *
 * ⚠️ 支付与履约是**两个正交状态**，这里合成一句人话给用户看 ——
 *    用户不该看到"已支付 / 待发货"两个标签自己去拼。
 */
export function OrderStatus({ payment, fulfillment }: { payment: string; fulfillment: string }) {
  const text =
    fulfillment === "cancelled"
      ? "已取消"
      : payment === "pending"
        ? "待付款"
        : payment === "refunded"
          ? "已退款"
          : payment === "partial_refunded"
            ? "部分退款"
            : fulfillment === "pending"
              ? "待发货"
              : fulfillment === "shipped"
                ? "待收货"
                : fulfillment === "delivered"
                  ? "已收货"
                  : "已完成";

  /** 待付款要显眼（还没拿到钱），终止态一律灰掉。 */
  const variant =
    text === "待付款"
      ? "warning"
      : text === "已取消" || text === "已退款"
        ? "secondary"
        : text === "部分退款"
          ? "destructive"
          : text === "待发货" || text === "待收货"
            ? "brand"
            : "success";

  return <Badge variant={variant}>{text}</Badge>;
}

/**
 * 带兜底的图片。
 *
 * ⚠️ 建材商品图由供应商自己传，**破图是常态**：文件被删、key 写错、
 *    对象存储临时不可达。不兜底的话页面上会出现一片浏览器默认的破图标，
 *    比空白还难看。加载失败就退回灰底占位。
 */
export function Img({
  src,
  alt,
  className = "",
}: {
  src?: string | null;
  alt: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className={cn("flex items-center justify-center bg-secondary", className)}>
        <ImageOff className="size-5 text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={className} />
  );
}

/**
 * 一行「标签 — 值」。金额明细、收货信息、订单字段都用它。
 *
 * ⚠️ 值靠右并且允许换行 —— 长地址不换行会把卡片撑破。
 */
export function Row({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-start justify-between gap-4 py-2 text-[14px]", className)}>
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right break-anywhere">{children}</span>
    </div>
  );
}

/** 分组小标题。⚠️ 手机上用小号灰字，别用大标题占掉一行高度。 */
export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between px-1 pt-4 pb-2">
      <h2 className="text-[13px] font-medium text-muted-foreground">{children}</h2>
      {action}
    </div>
  );
}

export { Badge };
